<template>
    <keep-alive>
        <KeepableSlot v-if="isActive">
            <!--
            Since `keep-alive` only works with Vue components and not with
            DOM elements we use this wrapper in order to make it cacheable
            -->
            <component
                :is="component"
                v-if="component && !lock"
            />
            <slot v-else></slot>
        </KeepableSlot>
    </keep-alive>
</template>

<script lang="ts">
import type { ComponentPublicInstance, PropType } from 'vue';
import { defineComponent, inject } from 'vue';
import {
    provideActiveTabIdKey,
    provideRegisterTabKey,
    provideUnregisterTabKey,
    provideUpdateTabKey,
} from '@/components/keys';
import KeepableSlot from './KeepableSlot.vue';

export default defineComponent({
    name: 'TabsPanelTab',
    components: {
        KeepableSlot,
    },
    props: {
        /**
         * Tab ID
         */
        id: {
            required: true,
            type: String,
        },
        /**
         * Tab name
         */
        name: {
            required: true,
            type: String,
        },
        /**
         * Boolean option to display a progress
         */
        progress: {
            required: false,
            default: false,
            type: Boolean,
        },
        /**
         * a locked tab will not reveal its content
         */
        lock: {
            type: Boolean,
            required: false,
            default: false,
        },
        /**
         * a locked tab text will not reveal its content
         */
        lockTabText: {
            type: Boolean,
            required: false,
            default: false,
        },
        /**
         * provide dynamic component import for async loading support
         */
        component: {
            type: [Function, Object] as PropType<ComponentPublicInstance | (() => ComponentPublicInstance)>,
            default: undefined,
        },
        /**
         * Tab icon to display
         */
        icon: {
            type: String,
            default: undefined,
        },
        /**
         * Tab icon to display
         */
        iconTestId: {
            type: String,
            default: null,
        },
        /**
         * Test ID
         */
        testId: {
            type: String,
            default: null,
        },
    },
    setup() {
        return {
            registerTab: inject(provideRegisterTabKey),
            updateTab: inject(provideUpdateTabKey),
            unregisterTab: inject(provideUnregisterTabKey),
            activeTabId: inject(provideActiveTabIdKey),
        };
    },
    computed: {
        isActive() {
            return this.activeTabId === this.id;
        },
        tabData() {
            return {
                id: this.id,
                name: this.name,
                icon: this.icon,
                progress: this.progress,
                lock: this.lock,
                testId: this.testId,
                lockTabText: this.lockTabText,
            };
        },
    },
    watch: {
        tabData(val) {
            this.updateTab?.(val);
        },
    },
    beforeMount() {
        this.registerTab?.(this.tabData);
    },
    beforeUnmount() {
        this.unregisterTab?.(this.id);
    },
});
</script>
